import { and, type Collection, createLiveQueryCollection, eq, type NonSingleResult } from "@tanstack/db";

import { store } from "@/store/store";
import type { WorkspaceMembershipTag } from "@/store/types";

type MembershipTagRecord = { workspaceMembershipId: string; tagId: string; tagValue: string | null; enabled: boolean };

function withValue(records: MembershipTagRecord[]): WorkspaceMembershipTag[] {
  const rows: WorkspaceMembershipTag[] = [];
  for (const { workspaceMembershipId, tagId, tagValue, enabled } of records) {
    if (tagValue == null) continue;
    // Records arrive through unchecked casts; normalize so the strict eq(enabled, true) live
    // query holds even if a producer ever emits int-as-boolean flags.
    rows.push({ workspaceMembershipId, tagId, tagValue, enabled: Boolean(enabled) });
  }
  return rows;
}

export async function bulkUpsertWorkspaceMembershipTag(records: MembershipTagRecord[]) {
  const rows = withValue(records);
  if (rows.length === 0) return;
  store.upsert("workspaceMembershipTag", rows);
}

export async function replaceAllWorkspaceMembershipTags(records: MembershipTagRecord[]) {
  // Skip disabled rows — the collection is wiped before insert, so there is nothing to tombstone.
  const rows = withValue(records).filter((r) => r.enabled);
  store.batch(() => {
    store.clear("workspaceMembershipTag");
    store.upsert("workspaceMembershipTag", rows);
  });
}

export type WorkspaceMembershipTagRow = {
  workspaceMembershipId: string;
  tagId: string;
  tagValue: string;
  tagLabel: string;
};

/**
 * Enabled membership tags joined to their enabled tag definitions — the incremental live-query
 * replacement for the old membershipTag-to-tag selector loop.
 */
// Annotated because the inferred live-query type references @tanstack/db internals that TS
// refuses to name in the emitted declaration (TS2742).
export const workspaceMembershipTagsQuery: Collection<WorkspaceMembershipTagRow> & NonSingleResult =
  createLiveQueryCollection({
    // Kept warm for the same reason as enabledTagsQuery: the driver grid reads it on every visit.
    startSync: true,
    gcTime: 0,
    query: (q) =>
      q
        .from({ membershipTag: store.collection("workspaceMembershipTag") })
        .innerJoin({ tag: store.collection("tag") }, ({ membershipTag, tag }) => eq(membershipTag.tagId, tag.id))
        .where(({ membershipTag, tag }) => and(eq(membershipTag.enabled, true), eq(tag.enabled, true)))
        .select(({ membershipTag, tag }) => ({
          workspaceMembershipId: membershipTag.workspaceMembershipId,
          tagId: membershipTag.tagId,
          tagValue: membershipTag.tagValue,
          tagLabel: tag.label,
        })),
  });
